import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Plus, Printer } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import {
  TIMECLOCK_PUNCH_KIND_VALUES,
  type TimeclockMark,
  type TimeclockPunchKind,
  type TimeclockSheetDay,
} from '@aeris/contracts'
import type { PrintDocument } from '@aeris/printing'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  EmptyState,
  Field,
  InlineAlert,
  Input,
  QueryState,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  notify,
} from '@aeris/ui'

import {
  ApiError,
  createTimeclockAdjustment,
  fetchTimeclockMembers,
  fetchTimeclockSheet,
} from '../lib/api.ts'
import { PUNCH_LABELS } from '../lib/ponto-device.ts'
import {
  SHEET_COLUMNS,
  clockText,
  dayText,
  minutesText,
  sheetPrintFor,
  shiftText,
} from '../lib/ponto-sheet.ts'
import { useCompanyHeader } from '../lib/use-receipt-template.ts'

import { ExportMenu } from './export-menu.tsx'
import { PrintDocumentDialog } from './print-document-dialog.tsx'

/**
 * O espelho do mês (F340, com a escala da F341): cada dia de um funcionário,
 * as marcas como valem, as horas e a comparação com a escala. O gerente
 * inclui a batida esquecida ou anula a errada — sempre com motivo; a batida
 * original nunca some, aparece riscada.
 */

const thisMonth = (): string =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' })
    .format(new Date())
    .slice(0, 7)

const messageOf = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

const SHEET_KEY = ['timeclock', 'sheet']

const IncludeDialog = ({
  employeeId,
  day,
  onClose,
}: {
  employeeId: string
  day: TimeclockSheetDay
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [kind, setKind] = useState<TimeclockPunchKind>('entrada')
  const [time, setTime] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async () => {
      await createTimeclockAdjustment({
        action: 'incluir',
        employeeId,
        kind,
        // A hora de parede de São Paulo; o Brasil está em -03:00.
        markedAt: `${day.day}T${time}:00-03:00`,
        reason,
      })
    },
    onSuccess: async () => {
      notify.success('Batida incluída.')
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(messageOf(cause, 'Não foi possível incluir.'))
    },
  })
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Incluir batida — ${dayText(day.day)}`}
        description="A batida que a pessoa esqueceu. Fica marcada como incluída pelo gerente, com o motivo."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Tipo" required>
              {(field) => (
                <Select
                  {...field}
                  value={kind}
                  onChange={(event) => {
                    setKind(event.target.value as TimeclockPunchKind)
                  }}
                >
                  {TIMECLOCK_PUNCH_KIND_VALUES.map((option) => (
                    <option key={option} value={option}>
                      {PUNCH_LABELS[option]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label="Hora" required>
              {(field) => (
                <Input
                  {...field}
                  type="time"
                  value={time}
                  onChange={(event) => {
                    setTime(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>
          <Field
            label="Motivo"
            required
            hint="Por exemplo: esqueceu de bater a saída."
          >
            {(field) => (
              <Input
                {...field}
                value={reason}
                maxLength={300}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={time === '' || reason.trim().length < 3}
            >
              Incluir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const VoidDialog = ({
  mark,
  onClose,
}: {
  mark: TimeclockMark
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: async () => {
      await createTimeclockAdjustment(
        mark.source === 'batida'
          ? { action: 'anular', punchId: mark.id, reason }
          : { action: 'anular', adjustmentId: mark.id, reason }
      )
    },
    onSuccess: async () => {
      notify.success('Batida anulada. Ela continua no histórico, riscada.')
      await queryClient.invalidateQueries({ queryKey: ['timeclock'] })
      onClose()
    },
    onError: (cause: unknown) => {
      setError(messageOf(cause, 'Não foi possível anular.'))
    },
  })
  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        title={`Anular ${PUNCH_LABELS[mark.kind]} das ${clockText(mark.at)}`}
        description="A batida não some: fica riscada no espelho, com o motivo e quem anulou."
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            save.mutate()
          }}
        >
          {error === null ? null : (
            <InlineAlert tone="danger">{error}</InlineAlert>
          )}
          {mark.photoUrl === null ? null : (
            <img
              src={mark.photoUrl.replace('/thumb', '/full')}
              alt={`Foto da batida das ${clockText(mark.at)}`}
              className="max-h-48 self-start rounded-(--radius-md) object-cover"
            />
          )}
          <Field label="Motivo" required hint="Por exemplo: bateu por engano.">
            {(field) => (
              <Input
                {...field}
                value={reason}
                maxLength={300}
                onChange={(event) => {
                  setReason(event.target.value)
                }}
              />
            )}
          </Field>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="danger"
              loading={save.isPending}
              disabled={reason.trim().length < 3}
            >
              Anular
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const MarkChip = ({
  mark,
  onVoid,
}: {
  mark: TimeclockMark
  onVoid: (() => void) | null
}): ReactNode => {
  const voided = mark.voided !== null
  const label = `${PUNCH_LABELS[mark.kind]} ${clockText(mark.at)}`
  const title = voided
    ? `Anulada por ${mark.voided?.by ?? ''}: ${mark.voided?.reason ?? ''}`
    : mark.source === 'ajuste'
      ? `Incluída por ${mark.by ?? ''}: ${mark.reason ?? ''}`
      : undefined
  const content = (
    <span className={voided ? 'line-through opacity-60' : undefined}>
      {label}
      {mark.source === 'ajuste' ? ' *' : ''}
    </span>
  )
  return onVoid === null || voided ? (
    <span
      title={title}
      className="rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-0.5 text-(length:--text-sm)"
    >
      {content}
    </span>
  ) : (
    <button
      type="button"
      title={title ?? 'Anular esta batida'}
      onClick={onVoid}
      className="rounded-(--radius-sm) bg-(--surface-sunken) px-2 py-0.5 text-(length:--text-sm) hover:bg-(--border-default)"
    >
      {content}
    </button>
  )
}

export const PontoEspelho = ({
  canManage,
}: {
  canManage: boolean
}): ReactNode => {
  const company = useCompanyHeader()
  const [employeeId, setEmployeeId] = useState('')
  const [month, setMonth] = useState(thisMonth)
  const [including, setIncluding] = useState<TimeclockSheetDay | null>(null)
  const [voiding, setVoiding] = useState<TimeclockMark | null>(null)
  const [printable, setPrintable] = useState<PrintDocument | null>(null)

  const members = useQuery({
    queryKey: ['timeclock', 'members'],
    queryFn: async ({ signal }) => await fetchTimeclockMembers(signal),
  })
  const people = (members.data?.members ?? []).filter(
    (row) => row.member !== null
  )
  const chosen = employeeId === '' ? (people[0]?.employeeId ?? '') : employeeId

  const sheet = useQuery({
    queryKey: [...SHEET_KEY, chosen, month],
    queryFn: async ({ signal }) =>
      await fetchTimeclockSheet(chosen, month, signal),
    enabled: chosen !== '' && /^\d{4}-\d{2}$/u.test(month),
  })
  const data = sheet.data

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Funcionário">
          {(field) => (
            <Select
              {...field}
              value={chosen}
              onChange={(event) => {
                setEmployeeId(event.target.value)
              }}
            >
              {people.map((row) => (
                <option key={row.employeeId} value={row.employeeId}>
                  {row.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Mês">
          {(field) => (
            <Input
              {...field}
              type="month"
              value={month}
              onChange={(event) => {
                // Limpar o campo desligaria a consulta: fica o mês de antes.
                if (event.target.value !== '') setMonth(event.target.value)
              }}
            />
          )}
        </Field>
        {data === undefined ? null : (
          <div className="flex gap-2">
            <Button
              variant="secondary"
              leadingIcon={<Printer aria-hidden="true" className="h-4 w-4" />}
              onClick={() => {
                setPrintable(sheetPrintFor(data, company))
              }}
            >
              Imprimir
            </Button>
            <ExportMenu
              fileName={`ponto-${data.employee.name}-${data.month}`}
              sheetName="Espelho"
              columns={SHEET_COLUMNS}
              fetchPage={() =>
                Promise.resolve({ items: data.days, total: data.days.length })
              }
              pageSize={data.days.length}
              total={data.days.length}
            />
          </div>
        )}
      </div>

      {people.length === 0 && members.isSuccess ? (
        <EmptyState
          title="Ninguém no ponto ainda"
          description="Cadastre o código de cada pessoa em Funcionários."
        />
      ) : (
        <QueryState
          isPending={members.isPending || (chosen !== '' && sheet.isPending)}
          isError={members.isError || sheet.isError}
          error={members.error ?? sheet.error}
          onRetry={() => {
            void members.refetch()
            if (chosen !== '') void sheet.refetch()
          }}
          what="o espelho do mês"
          isEmpty={false}
          empty={null}
        >
          {data === undefined ? null : (
            <>
              <p className="flex flex-wrap gap-2 text-(length:--text-sm)">
                <Badge tone="neutral">
                  Trabalhado {minutesText(data.totals.workedMinutes)}
                </Badge>
                <Badge tone="neutral">
                  Previsto {minutesText(data.totals.scheduledMinutes)}
                </Badge>
                <Badge tone={data.totals.extraMinutes > 0 ? 'info' : 'neutral'}>
                  Extra {minutesText(data.totals.extraMinutes)}
                </Badge>
                <Badge
                  tone={
                    data.totals.lateMinutes + data.totals.earlyMinutes > 0
                      ? 'warning'
                      : 'neutral'
                  }
                >
                  Atraso e saída antes{' '}
                  {minutesText(
                    data.totals.lateMinutes + data.totals.earlyMinutes
                  )}
                </Badge>
                <Badge
                  tone={data.totals.shortMinutes > 0 ? 'warning' : 'neutral'}
                >
                  Faltou {minutesText(data.totals.shortMinutes)}
                </Badge>
                <Badge tone={data.totals.absences > 0 ? 'danger' : 'neutral'}>
                  {data.totals.absences} falta(s)
                </Badge>
                <Badge
                  tone={data.totals.incompleteDays > 0 ? 'warning' : 'neutral'}
                >
                  {data.totals.incompleteDays} dia(s) incompleto(s)
                </Badge>
              </p>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Dia</TableHeaderCell>
                      <TableHeaderCell>Escala</TableHeaderCell>
                      <TableHeaderCell>Marcas</TableHeaderCell>
                      <TableHeaderCell>Trabalhado</TableHeaderCell>
                      <TableHeaderCell>Atraso</TableHeaderCell>
                      <TableHeaderCell>Extra</TableHeaderCell>
                      <TableHeaderCell>Situação</TableHeaderCell>
                      {canManage ? (
                        <TableHeaderCell>Ações</TableHeaderCell>
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.days.map((day) => (
                      <TableRow key={day.day}>
                        <TableCell className="whitespace-nowrap">
                          {dayText(day.day)}
                        </TableCell>
                        <TableCell>{shiftText(day.shift)}</TableCell>
                        <TableCell>
                          <span className="flex flex-wrap gap-1">
                            {day.marks.map((mark) => (
                              <MarkChip
                                key={mark.id}
                                mark={mark}
                                onVoid={
                                  canManage
                                    ? () => {
                                        setVoiding(mark)
                                      }
                                    : null
                                }
                              />
                            ))}
                          </span>
                        </TableCell>
                        <TableCell>{minutesText(day.workedMinutes)}</TableCell>
                        <TableCell>
                          {minutesText(day.lateMinutes + day.earlyMinutes)}
                        </TableCell>
                        <TableCell>{minutesText(day.extraMinutes)}</TableCell>
                        <TableCell>
                          <span className="flex flex-wrap gap-1">
                            {day.absent ? (
                              <Badge tone="danger">Falta</Badge>
                            ) : null}
                            {day.incomplete ? (
                              <Badge tone="warning">Incompleto</Badge>
                            ) : null}
                            {day.shortMinutes > 0 ? (
                              <Badge tone="warning">
                                Faltou {minutesText(day.shortMinutes)}
                              </Badge>
                            ) : null}
                            {day.note === 'trabalhou_na_folga' ? (
                              <Badge tone="info">Trabalhou na folga</Badge>
                            ) : null}
                            {day.note === 'folga' ? (
                              <Badge tone="neutral">Folga</Badge>
                            ) : null}
                            {day.adjusted ? (
                              <Badge tone="neutral">Ajustado</Badge>
                            ) : null}
                          </span>
                        </TableCell>
                        {canManage ? (
                          <TableCell>
                            <Button
                              size="sm"
                              variant="ghost"
                              leadingIcon={
                                <Plus aria-hidden="true" className="h-4 w-4" />
                              }
                              onClick={() => {
                                setIncluding(day)
                              }}
                            >
                              Incluir
                            </Button>
                          </TableCell>
                        ) : null}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <p className="text-(length:--text-sm) text-(--text-secondary)">
                * incluída pelo gerente.
                {canManage
                  ? ' Clique numa marca para anular; ela continua no histórico, riscada.'
                  : null}{' '}
                Atraso, extra e o que faltou seguem a tolerância da CLT: até 5
                minutos por marcação e 10 por dia não contam.
              </p>
            </>
          )}
        </QueryState>
      )}

      {including === null ? null : (
        <IncludeDialog
          employeeId={chosen}
          day={including}
          onClose={() => {
            setIncluding(null)
          }}
        />
      )}
      {voiding === null ? null : (
        <VoidDialog
          mark={voiding}
          onClose={() => {
            setVoiding(null)
          }}
        />
      )}
      {printable === null ? null : (
        <PrintDocumentDialog
          document={printable}
          description="O espelho do mês, para o funcionário assinar"
          initialPaper="a4"
          papers={['a4']}
          onClose={() => {
            setPrintable(null)
          }}
        />
      )}
    </div>
  )
}
